/**
 * @module employee.actions
 */
import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { normalize } from 'normalizr';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import { httpRequest } from '~/adapters/httpRequest';
import { convert } from '~/tools/convert';
import { list } from '~/tools/list';
import { datetime } from '~/tools/datetime';
import { validate } from '~/tools/validate';
import { text } from '~/tools/text';
import { keys as attributeTypeKeys } from '~/config/settings/attribute_types.settings';
import { importTypes } from '~/config/settings/employee.settings';
import { statuses as notificationStatuses } from '~/config/settings/notification.settings';
import { host } from '~/config/settings/worktango.settings';
import { employeeAttributeActions } from './employee_attribute.actions';
import * as l10n from '../languages';
import * as transformers from '../transformers';
import * as schemas from '../schemas';
import {
  CreateEmployeeParams,
  EmployeeAttributeObject,
  EmployeeDict,
  ExportEmployeesResponse,
  FetchEmployeeOptions,
  FetchEmployeesOptions,
  NormalizedEmployees,
  PageEmployeeOptions,
  ModifyEmployeeParams,
  LanguageDict,
  ImportEmployeesValidationResponse,
  EmployeeAttribute,
  ImportEmployeesWarningValidationResponse,
} from '~/interfaces/employee.interface';
import type { AppDispatch, RootState } from '../store';
import {
  CreateEmployeeDocument,
  FetchCompanyEmployeeIdsDocument,
  FetchCompanyEmployeesAndUsersDocument,
  FetchEmployeesDocument,
  FetchEmployeesWithoutAttributesDocument,
  ImportEmployeesDocument,
  NotifyPrivateAttributeExportDocument,
  UpdateCompanyEmployeeIdsDocument,
  UpdateEmployeeDocument,
} from '~/gql/graphql';
import * as excel from '~/adapters/excel';
import { statuses } from '~/config/settings/user.settings';
import { coalesce } from '~/helpers/coalesce';

const CUSTOM_PERMISSIONS_STRING = 'CUSTOM_PERMISSIONS';

// The backend throws this exact prefix when one or more `isUnique` attribute
// values collide with an existing employee (see `editEmployeeAttributesWithValues`
// in the API's `employee-repository`). The remainder of the message is a
// comma-separated list of attribute IDs.
const UNIQUE_ATTRIBUTE_ERROR_PREFIX = 'Duplicated values for following unique attribute Ids: ';

const buildUniqueAttributeErrorNotification = (
  errorMessage: string,
  employeeAttributes: { [id: string]: { name?: string } },
) => {
  if (typeof errorMessage !== 'string' || !errorMessage.startsWith(UNIQUE_ATTRIBUTE_ERROR_PREFIX)) {
    return null;
  }

  const names = errorMessage
    .slice(UNIQUE_ATTRIBUTE_ERROR_PREFIX.length)
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => employeeAttributes[id]?.name ?? id);

  if (names.length === 0) return null;

  return {
    title: l10n.t('form.invalid'),
    message: `${l10n.t('form.invalidAttribute', { name: names.join(', ') })}. ${l10n.t('form.isUnique')}.`,
    status: notificationStatuses.error,
  };
};

const fetchEmployeeEntities = async (
  companyId: string,
  /* istanbul ignore start -- defaults are never used; all callers pass explicit args */
  includeAttributeValues: boolean = false,
  languages = {},
  options: FetchEmployeeOptions = {} as FetchEmployeeOptions,
  /* istanbul ignore stop */
) => {
  const {
    surveyTypeId,
    search,
    batchSearch,
    attributeValues,
    employeeIds,
    offset,
    limit,
    // order, TODO: implement sorting logic once backend supports it
    // orderBy,
    filterBy: { isActive },
  } = options;
  const formattedAttributeValues = attributeValues
    ? transformers.attributeValuesToGraphqlFormat(attributeValues)
    : null;
  const queryDocument = includeAttributeValues ? FetchEmployeesDocument : FetchEmployeesWithoutAttributesDocument;

  const { data: res } = await graphqlRequest<any>(queryDocument, {
    companyId,
    surveyTypeId,
    search,
    batchSearch,
    attributes: formattedAttributeValues,
    employeeIds,
    limit,
    offset,
    enabled: isActive,
  });

  // @ts-ignore
  if (res?.error) throw new Error('Unable to fetch employees', res.error);

  const rawEmployees = res?.employeeDetails?.results;
  const employeeCount = res?.employeeDetails?.count;

  // WARNING: Add language ISO-code to each employee as attribute `lang`. If
  // unable to find a matching ISO-code in `languages` for `employee.languageId`
  // the app's `defaultLang` will be used instead. (from the transformer).
  //
  // OPTIMIZE: Update server so `lang` ISO-code is returned by default to avoid
  // this transformation for better performance.
  const rawEmployeesWithLang = rawEmployees.map((employee) => ({
    ...employee,
    lang: languages[employee.languageId]?.code,
  }));

  const employees = transformers.employeesFrom(rawEmployeesWithLang);
  const normalizedData = normalize(employees, [schemas.employee]);

  return {
    ...normalizedData,
    count: employeeCount,
  };
};

// Fetch a single employee.
// REFACTOR: can this be simplified to share logic with `fetchEmployees()`?
const fetchEmployee = createAsyncThunk<EmployeeDict, { companyId?: string; employeeId?: number }, { state: RootState }>(
  'employee/fetchEmployee',
  async ({ companyId, employeeId }, { rejectWithValue, getState }) => {
    if (!companyId) return rejectWithValue('`companyId` is required');

    const { data: res } = await graphqlRequest<any>(FetchEmployeesDocument, {
      companyId,
      employeeIds: [employeeId],
      limit: 1,
    });

    if (res?.error) return rejectWithValue(res.error);

    const rawEmployee = res?.employeeDetails?.results[0];

    if (!rawEmployee)
      return rejectWithValue(`no employee record for employeeId ${employeeId} and companyId ${companyId}`);

    const languages = getState().entities.languages;
    const employee = transformers.employeeFrom({
      ...rawEmployee,
      lang: languages[rawEmployee.languageId]?.code,
    });
    const normalizedData = normalize(employee, schemas.employee);

    return normalizedData;
  },
);

// Fetch multiple employees by page.
const FETCH_ALL_PAGE_SIZE = 10000;

const fetchEmployees = createAsyncThunk<NormalizedEmployees, FetchEmployeesOptions, { state: RootState }>(
  'employee/fetchEmployees',
  async (
    {
      companyId,
      surveyTypeId,
      search,
      batchSearch,
      limit,
      offset,
      attributeValues,
      employeeIds,
      isActive,
      includeAttributes = true,
      fetchAll = false,
    } = {},
    { rejectWithValue, getState },
  ) => {
    if (!companyId) return rejectWithValue('`companyId` is required');

    const account = getState().account;
    const languages = getState().entities.languages;

    const baseOptions = {
      surveyTypeId,
      search,
      batchSearch,
      attributeValues,
      employeeIds,
      filterBy: {
        // @ts-ignore @TODO Epic WT-21600
        isActive,
      },
    };

    try {
      if (!fetchAll) {
        // @ts-ignore @TODO Epic WT-21600
        return await fetchEmployeeEntities(account.companyId, includeAttributes, languages, {
          ...baseOptions,
          offset,
          limit,
        });
      }

      // Paginate through all employees, merging each page into a combined result.
      const combinedEmployees: EmployeeDict = {};
      const combinedResult: number[] = [];
      let currentOffset = 0;
      let total = Infinity;

      while (currentOffset < total) {
        // @ts-ignore @TODO Epic WT-21600
        const page = await fetchEmployeeEntities(account.companyId, includeAttributes, languages, {
          ...baseOptions,
          offset: currentOffset,
          limit: FETCH_ALL_PAGE_SIZE,
        });

        if (currentOffset === 0) total = page.count ?? 0;
        Object.assign(combinedEmployees, page.entities?.employees ?? {});
        combinedResult.push(...(page.result as number[]));
        currentOffset += FETCH_ALL_PAGE_SIZE;
      }

      return { entities: { employees: combinedEmployees }, result: combinedResult, count: total };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

// TODO: Server doesn't currently support ordering/sorting so for now, simply
// preserving whatever order backend has is using (assuming it is by id?)
const pageEmployees = createAsyncThunk<EmployeeDict, PageEmployeeOptions, { state: RootState }>(
  'employee/pageEmployees',
  async ({ attributeValues, filterBy, orderBy, order, page, per, search } = {}, { rejectWithValue, getState }) => {
    const account = getState().account;
    const employeeUI = getState().ui.employees;
    const languages = getState().entities.languages;

    // Handle fallback values: use provided value, else use value already chosen
    // in UI state, else use default or empty value.
    const activeSearch = coalesce(coalesce(search, employeeUI.search), '');
    const activeFilterBy = coalesce(coalesce(filterBy, employeeUI.filterBy), { isActive: true });
    const activeOrderBy = coalesce(coalesce(orderBy, employeeUI.orderBy), 'name');
    const activeOrder = coalesce(coalesce(order, employeeUI.order), 'asc');
    const activeAttributeValues = coalesce(coalesce(attributeValues, employeeUI.attributeValues), {});
    const activePage = coalesce(coalesce(page, employeeUI.page), 0);
    const limit = coalesce(coalesce(per, employeeUI.per), 15);
    const offset = activePage * limit;
    const isActive = activeFilterBy.isActive === 'true' || activeFilterBy.isActive === true;

    try {
      // @ts-ignore @TODO Epic WT-21600
      const employeeData = await fetchEmployeeEntities(account.companyId, true, languages, {
        search: activeSearch,
        attributeValues: activeAttributeValues,
        offset,
        limit,
        filterBy: {
          isActive,
        },
      });
      const employeeIds = employeeData?.entities?.employees
        ? Object.keys(employeeData.entities.employees).map(Number)
        : [];

      return {
        ...employeeData,
        pageEmployeeIds: employeeIds,
        pagination: {
          search: activeSearch,
          attributeValues: activeAttributeValues,
          filterBy: activeFilterBy,
          orderBy: activeOrderBy,
          order: activeOrder,
          page: activePage,
          per: limit,
          count: employeeData?.count,
        },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const createEmployee = createAsyncThunk<EmployeeDict, CreateEmployeeParams, { state: RootState }>(
  'employee/createEmployee',
  async ({ companyId, isActive, lang, employeeAttributeValues }, { rejectWithValue, getState }) => {
    const state = getState();
    const employeeAttributes = state.entities.employeeAttributes;
    const languages = state.entities.languages;

    const enabled = Boolean(isActive);
    const languageId = Number(languages?.codeIds[lang]);
    const attributesWithValues = Object.keys(employeeAttributeValues).map((attrId) => ({
      id: Number(attrId),
      action: 'CREATE',
      type: employeeAttributes[attrId].type,
      value: employeeAttributeValues[attrId],
    }));

    try {
      const { data: res } = await graphqlRequest<any>(CreateEmployeeDocument, {
        companyId,
        employee: {
          enabled,
          languageId,
          attributesWithValues,
        },
      });

      const rawNewEmployee = res?.addEmployee;
      rawNewEmployee.lang = languages.codeIds[rawNewEmployee.languageId];

      const employee = transformers.updatedEmployeeFrom(rawNewEmployee);
      const normalizedData = normalize(employee, schemas.employee);

      return {
        ...normalizedData,
        notification: {
          title: l10n.t('notifications.title.done'),
          message: l10n.t('notifications.message.employeeCreated'),
          status: notificationStatuses.success,
        },
      };
    } catch (error) {
      const errorMessage = coalesce((error as { message?: string })?.message, '');
      const notification = coalesce(buildUniqueAttributeErrorNotification(errorMessage, employeeAttributes), {
        title: l10n.t('form.invalid'),
        message: errorMessage || l10n.t('error.somethingWentWrong'),
        status: notificationStatuses.error,
      });

      return rejectWithValue({ notification });
    }
  },
);

const modifyEmployee = createAsyncThunk<EmployeeDict, ModifyEmployeeParams, { state: RootState }>(
  'employee/modifyEmployee',
  async ({ employeeId, companyId, isActive, lang, employeeAttributeValues }, { rejectWithValue, getState }) => {
    const state = getState();
    const employeeAttributes = state.entities.employeeAttributes;
    const languages = state.entities.languages;

    const enabled = Boolean(isActive);
    const languageId = Number(languages?.codeIds[lang]);

    const attributesWithValues = Object.keys(employeeAttributeValues).map((attrId) => {
      const id = Number(attrId);
      const type = employeeAttributes[attrId].type;
      const value = employeeAttributeValues[attrId];
      const action = value === undefined || value === null || value === '' ? 'DELETE' : 'UPDATE';

      return {
        id,
        action,
        type,
        value,
      };
    });
    try {
      const { data: res } = await graphqlRequest<any>(UpdateEmployeeDocument, {
        employeeId,
        companyId,
        employee: {
          enabled,
          languageId,
          attributesWithValues,
        },
      });

      if (res?.error) return rejectWithValue(res.error);

      const rawUpdatedEmployee = res?.updateEmployee;
      rawUpdatedEmployee.lang = coalesce(languages[rawUpdatedEmployee.languageId]?.code, 'en-ca');
      const employee = transformers.updatedEmployeeFrom(rawUpdatedEmployee);
      const normalizedData = normalize(employee, schemas.employee);
      return {
        ...normalizedData,
        notification: {
          title: l10n.t('notifications.title.done'),
          message: l10n.t('notifications.message.employeeUpdates'),
          status: notificationStatuses.success,
        },
      };
    } catch (error) {
      const errorMessage = coalesce((error as { message?: string })?.message, '');
      const notification = coalesce(buildUniqueAttributeErrorNotification(errorMessage, employeeAttributes), {
        title: l10n.t('form.invalid'),
        message: errorMessage || l10n.t('error.somethingWentWrong'),
        status: notificationStatuses.error,
      });

      return rejectWithValue({ notification });
    }
  },
);

const removeEmployee = createAsyncThunk(
  'employee/removeEmployee',
  async ({ employeeId, companyId }: { employeeId: number; companyId: number }) => {
    return {};
  },
);

/**
 * Make a network request for employee data, based on provided options, and
 * convert the received data into an Excel file and trigger a download in
 * the browser.
 */
const exportEmployees = createAsyncThunk<
  ExportEmployeesResponse,
  {
    includePrivateAttributes?: boolean;
    includeDatabaseEmployeeId?: boolean;
    includeArchivedAttributes?: boolean;
  },
  { state: RootState; dispatch: AppDispatch }
>(
  'employee/exportEmployees',
  async (
    { includePrivateAttributes = false, includeDatabaseEmployeeId = false, includeArchivedAttributes = false },
    { getState, dispatch, rejectWithValue },
  ) => {
    const ACTIVE_KEY = 'Enabled';
    const LANGUAGE_KEY = 'Language';
    const DB_EMPLOYEE_ID_KEY = 'LegacyPulseEmployeeId';

    const account = getState().account;

    // @ts-ignore @TODO Epic WT-21600
    const { codeIds, ...languages }: { codeIds: string[]; languages: LanguageDict } = getState().entities.languages;
    const {
      search,
      attributeValues,
      filterBy: { isActive },
    } = getState().ui.employees;

    let employeeAttributes: EmployeeAttributeObject[] = Object.values(getState().entities.employeeAttributes);

    // If no employeeAttributes are currently present in the state, make a new
    // network request to retreive them (this will also store the latest data in the state).
    if (employeeAttributes.length === 0) {
      const action = await dispatch(
        // @ts-ignore @TODO Epic WT-21600
        employeeAttributeActions.fetchEmployeeAttributes({
          // @ts-ignore @TODO Epic WT-21600
          companyId: account.companyId,
          includePrivateAttributes,
        }),
      );

      employeeAttributes = Object.values(action.payload.entities.employeeAttributes);
    }

    if (!includePrivateAttributes) {
      employeeAttributes = employeeAttributes.filter((attr) => !attr.isPrivate);
    }

    if (!includeArchivedAttributes) {
      // @ts-ignore @TODO Epic WT-21600
      employeeAttributes = employeeAttributes.filter((attr) => !attr.isArchived);
    }

    // Sort attributes first by alphabetical name, then group all "system"
    // attributes at the beginning of the list.
    employeeAttributes.sort(list.byAttribute('name', 'asc'));
    employeeAttributes.sort(list.byAttribute('baseAttributeId', 'asc'));

    try {
      if (includePrivateAttributes) {
        await graphqlRequest<any>(NotifyPrivateAttributeExportDocument, { companyId: account.companyId });
      }

      // @ts-ignore @TODO Epic WT-21600
      const employeeData = await fetchEmployeeEntities(account.companyId, true, languages, {
        filterBy: {
          isActive,
        },
        search,
        attributeValues,
      });

      // @ts-ignore @TODO Epic WT-21600
      const employees = employeeData && Object.values(employeeData?.entities?.employees);

      const status = isActive ? l10n.t('system.field.enabled') : l10n.t('system.field.disabled');

      const filenameSuffix = includePrivateAttributes ? '-with-private-attributes' : '';
      const filename = `Employees-${status}${filenameSuffix}`;

      const KW_EXPECTED_COLUMN_NAME = 'role';
      const KW_EXPECTED_ROLE_NAME = 'SURVEY_CUSTOM';

      const columnHeaders = [
        ...(includeDatabaseEmployeeId ? [DB_EMPLOYEE_ID_KEY] : []),
        ACTIVE_KEY,
        ...employeeAttributes.map((attr) => {
          const name = coalesce(attr.translations[account.lang].name, attr.name);
          return text.toTitlecase(name);
        }),
        LANGUAGE_KEY,
      ];
      /* istanbul ignore start -- workspace-only column (kazoo-web embedded mode) */
      if (account.workspace) {
        columnHeaders.push(KW_EXPECTED_COLUMN_NAME);
      }
      /* istanbul ignore stop */

      const employeesTab = {
        name: l10n.t('admin.employees.header'),
        titles: columnHeaders,
        rows: employees.map((employee) => {
          const columns = [
            ...(includeDatabaseEmployeeId ? [employee.id] : []),
            employee.isActive,
            ...employeeAttributes.map((attr) => {
              let value = employee.employeeAttributeValues[attr.id];

              if (value && attr.type === 'DATE') {
                const year = Number(value.substring(0, 4));
                const month = Number(value.substring(5, 7)) - 1;
                const day = Number(value.substring(8, 10));
                const date = new Date(year, month, day);
                value = datetime.format(date, 'yyyy-MM-dd');
              }
              return value;
            }),
            employee.lang,
          ];
          /* istanbul ignore start -- workspace-only role column (kazoo-web embedded mode) */
          if (account.workspace && employee.isUser) columns.push(KW_EXPECTED_ROLE_NAME);
          /* istanbul ignore stop */
          return columns;
        }),
      };

      const languagesTab = {
        name: l10n.t('languageCode'),
        titles: [l10n.t('system.field.language'), l10n.t('languageCode')],
        rows: [
          ...Object.values(languages).map((language) => [l10n.t(language.code), language.code]),
          [], // NOTE: appending an empty row to separate the description below from the regular data ;)
          [l10n.t('language.code.desc')],
        ],
      };

      // Will trigger browser download.
      excel.toFile(filename, [employeesTab, languagesTab]);

      return {
        filename,
        notification: {
          title: l10n.t('notifications.title.done'),
          message: l10n.t('employees.export.success', { filename: `${filename}.xlsx` }),
          status: notificationStatuses.success,
        },
      };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

const generateSampleEmployeeImport = createAction(
  'employee/generateSampleEmployeeImport',
  ({ employeeAttributes, supportedLanguages, lang }) => {
    // TODO: handle this in a more maintainable way.
    const ACTIVE_KEY = 'Enabled';
    const LANGUAGE_KEY = 'Language';

    const filename = 'Employees-Sample'; // TODO: maybe create a dynamic name that is more descriptive?

    const employeesTab = {
      name: l10n.t('admin.employees.header'),
      titles: [
        ACTIVE_KEY,
        ...employeeAttributes.map((attr) => {
          const name = attr.translations[lang].name || attr.name;

          return text.toTitlecase(name);
        }),
      ],
      rows: [
        [
          true, // `employee.isActive` (i.e., "enabled")
          ...employeeAttributes.map((attr) => convert.attributeTypeToSampleValue(attr.type)),
        ],
      ],
    };

    const languagesTab = {
      name: l10n.t('languageCode'),
      titles: [LANGUAGE_KEY, l10n.t('languageCode')],
      rows: [
        ...supportedLanguages.map((supportedLang) => [l10n.t(supportedLang), supportedLang]),
        [], // NOTE: appending an empty row to separate the description below from the regular data ;)
        [l10n.t('language.code.desc')],
      ],
    };

    // Will trigger browser download.
    excel.toFile(filename, [employeesTab, languagesTab]);

    return {
      payload: {
        filename,
      },
    };
  },
);

const getValidRoleNames = async (companyId: number) => {
  const rolesURL = `${host}/user-roles?companyId=${companyId}`;
  const response = await httpRequest<any>(rolesURL);
  const roles = response.payload.roles;

  return roles.reduce((acc, current) => {
    const languages = Object.keys(current.translations);
    const names = languages.map((lang) => current.translations[lang].name.toLowerCase());
    acc.push(...names);
    return acc;
  }, []);
};

const isUserDisabled = (user) => {
  const userNonDisabledStatuses = ['ACTIVE', 'INVITATION_PENDING'];
  return !userNonDisabledStatuses.includes(user?.status);
};

const validateEmployeesImportData = createAsyncThunk<
  { payload: { isValid: boolean; errorMsg?: string } } | unknown,
  {
    buffer: ArrayBuffer;
    employeeAttributes: EmployeeAttribute[];
    supportedLanguages: string[];
    companyId: number;
  }
>(
  'employee/validateEmployeesImportData',
  // @ts-ignore @TODO Epic WT-21600
  async ({ buffer, employeeAttributes, supportedLanguages, companyId, importType }, { rejectWithValue }) => {
    // TODO: Is there a better way of maintaining these key values?
    // WARNING: The expected key strings are explicitly defined here, but then
    // converted to lower case (along with all employee attribute keys) in order
    // to match without case sensitivity. This is not ideal and should be revised.
    const COMPANY_EMPLOYEE_ID_KEY = 'Employee Id'.toLowerCase();
    const LANGUAGE_KEY = 'Language'.toLowerCase();
    const ACTIVE_KEY = 'Enabled'.toLowerCase();
    const EMAIL_KEY = 'Email'.toLowerCase();
    const FIRST_NAME_KEY = 'First Name'.toLowerCase();
    const LAST_NAME_KEY = 'Last Name'.toLowerCase();
    const ROLE = '$Survey_Role'.toLowerCase();

    const validRoleNames = await getValidRoleNames(companyId);

    const paddingIndex = 2; // Header + indexes starting with 0
    const employees = excel
      .employeesToJson(buffer, { dateFormat: 'YYYY-MM-DD' })
      .map((employee, index) => ({
        rowIndex: index + paddingIndex,
        ...convert.toLowerKeys(employee),
      }))
      .filter(({ rowIndex, ...row }) => !excel.isRowEmpty(row));

    // We only use lower case for all company employee ids and emails in backend
    const validEmployees = employees.map((employee) => ({
      ...employee,
      [COMPANY_EMPLOYEE_ID_KEY]: coalesce(employee[COMPANY_EMPLOYEE_ID_KEY]?.toLowerCase().trim(), ''),
    }));

    // WARNING: appending pseudo employee attributes as valid import fields to be
    // treated *as if* they are employee attributes.
    const validAttributes = [
      {
        name: ACTIVE_KEY,
        type: attributeTypeKeys.BOOLEAN, // is valid if true, false, 'yes', 'no', 'True', 'False'
        isRequired: true,
        isUnique: false,
      },
      {
        name: LANGUAGE_KEY,
        type: attributeTypeKeys.STRING,
        isRequired: false,
        isUnique: false,
      },
      {
        name: ROLE,
        type: attributeTypeKeys.STRING,
        isRequired: false,
        isUnique: false,
      },
      ...employeeAttributes
        .map((attr) => ({
          ...attr,
          // This is done in order to match keys without case sensitivity.
          name: attr.name.toLowerCase(),
        }))
        .filter((attr) => {
          //Don't allow uploading archived attributes
          // @ts-ignore @TODO Epic WT-21600
          return !attr.isArchived;
        }),
    ];
    const validAttributeNames = validAttributes.map((attr) => attr.name);
    const uniqueAttributeNames = validAttributes.reduce((acc, attr) => {
      if (!attr.isUnique) return acc;

      return [...acc, attr.name];
    }, [] as string[]);

    // A collection of all individual validation errors detected for detailed
    // feedback to user so they can make corrections.
    const errors: ImportEmployeesValidationResponse = {
      duplicateUniqueValues: [],
      unknownAttributeNames: [],
      missingRequiredErrors: [],
      attributeTypeErrors: [],
      modifiedEmployeeIdErrors: [],
      unsupportedLangs: [],
      unknownRoleNamesRows: [],
      customPermissionsRows: [],
      userMissingEmailRows: [],
      invitingDisabledEmployee: [],
    };

    const warnings: ImportEmployeesWarningValidationResponse = {
      deactivateUsers: [],
      deactivateEmployees: [],
      grantAccess: [],
    };
    const usersToDeactivate = new Map();
    const employeesToDeactivate = new Map();
    const employeesToGrantAccess = new Map();

    // 1. Validate no duplicate unique attribute values (handles duplicate employee ids too).
    uniqueAttributeNames.forEach((attrName) => {
      const allEmployeeAttrValues = validEmployees.reduce((acc: string[], employee) => {
        const value = employee[attrName]?.toLowerCase();

        // don't compare empty values
        if (!value) return acc;

        return [...acc, value];
      }, []);

      list.duplicatesFrom(allEmployeeAttrValues).forEach((value) => {
        const error = `${attrName}: ${value}`;

        if (!errors.duplicateUniqueValues.includes(error)) {
          errors.duplicateUniqueValues.push(error);
        }
      });
    });

    const employeesDict = validEmployees.reduce((acc, emp) => {
      acc[emp[COMPANY_EMPLOYEE_ID_KEY]] = emp;
      return acc;
    }, {});
    const { data: existingEmployeesRes } = await graphqlRequest<any>(FetchCompanyEmployeesAndUsersDocument, {
      companyId,
    });
    if (!existingEmployeesRes?.employeeDetails?.results) {
      return rejectWithValue(`Unable to fetch company employee IDs for company ${companyId}`);
    }
    const existingEmployees = coalesce(existingEmployeesRes?.employeeDetails?.results, []);
    const existingEmployeesDict = existingEmployees.reduce((acc, employee) => {
      const key = employee.lastName + employee.email;
      if (!employee.email) return acc;
      acc[key] = employee;
      return acc;
    }, {});

    validEmployees.forEach(({ rowIndex, ...employee }) => {
      // Check if employee id has been modified, update employee id should use any function, now here.
      const employeeEmail = employee[EMAIL_KEY];
      const searchKey = employee[LAST_NAME_KEY] + employee[EMAIL_KEY];
      const employeeId = employee[COMPANY_EMPLOYEE_ID_KEY];
      const existingEmployee = employeeEmail ? existingEmployeesDict[searchKey] : null;
      if (existingEmployee && employeeId !== existingEmployee.employeeId) {
        const msg = l10n.t('employee.import.error.modifiedEmployeeId', {
          employeeName: employee[FIRST_NAME_KEY] + ' ' + employee[LAST_NAME_KEY],
          oldEmployeeId: existingEmployee.employeeId,
          newEmployeeId: employeeId,
        });
        errors.modifiedEmployeeIdErrors.push(msg);
      }
      // 2. Validate required fields have values (handles missing employee ids too).
      validAttributes.forEach((attr) => {
        if (attr.isRequired && !validate.hasAttributeValue(employee[attr.name])) {
          const msg = l10n.t('employee.import.error.missingField', {
            employeeId: employee[COMPANY_EMPLOYEE_ID_KEY],
            attrName: attr.name,
          });

          errors.missingRequiredErrors.push(msg);
        }
      });

      Object.keys(employee).forEach((attrName) => {
        const attr = validAttributes.find((validAttr) => validAttr.name.toLowerCase() === attrName.toLowerCase());

        // 3. Validate no unknown attribute names.
        if (!validAttributeNames.includes(attrName.toLowerCase())) {
          if (!errors.unknownAttributeNames.includes(attrName)) {
            errors.unknownAttributeNames.push(attrName);
          }

          return; // skip futher validating unknown attributes
        }

        // 4. Validate non-empty attribute values are the correct type.
        // @ts-ignore @TODO Epic WT-21600
        if (employee[attrName] && !validate.isAttributeType(attr?.type, employee[attrName])) {
          const msg = l10n.t('employee.import.error.invalidType', {
            employeeId: employee[COMPANY_EMPLOYEE_ID_KEY],
            attrType: attr?.type,
            attrName,
          });

          errors.attributeTypeErrors.push(msg);
        }

        // 5. Validate language is supported (if it is defined).
        if (
          attrName === LANGUAGE_KEY &&
          employee[LANGUAGE_KEY] &&
          !supportedLanguages.includes(employee[LANGUAGE_KEY]) &&
          !errors.unsupportedLangs.includes(employee[LANGUAGE_KEY])
        ) {
          errors.unsupportedLangs.push(employee[LANGUAGE_KEY]);
        }
      });

      // 6. Validate roles
      const hasRoleColumn = employee.hasOwnProperty(ROLE);
      const employeeRole = employee[ROLE]?.toLowerCase();
      const isCustomPermissions = employee[ROLE] && employee[ROLE] === CUSTOM_PERMISSIONS_STRING;
      const isRoleValid = validRoleNames.includes(employeeRole);

      if (hasRoleColumn && employeeRole && !isRoleValid && !isCustomPermissions) {
        errors.unknownRoleNamesRows.push(rowIndex);
      }

      if (
        hasRoleColumn &&
        isCustomPermissions &&
        (!existingEmployee || !existingEmployee.isUser || existingEmployee?.hasRole)
      ) {
        errors.customPermissionsRows.push(rowIndex);
      }

      const newUserInviteForDisabledEmployee =
        hasRoleColumn && employeeRole && !existingEmployee?.hasRole && employee?.['enabled'] !== 'Yes';
      if (newUserInviteForDisabledEmployee) {
        errors.invitingDisabledEmployee.push(rowIndex);
      }

      // 7. Check if users should be deactivated
      if (
        hasRoleColumn &&
        !employeeRole &&
        !isUserDisabled(existingEmployee?.user) &&
        !existingEmployee.user?.worktangoAdmin &&
        existingEmployee?.user?.status.toLowerCase() === statuses.active
      ) {
        usersToDeactivate.set(existingEmployee.user.email, existingEmployee.user);
      }
      // 8. Check if user can be changed/created
      const hasEmail = !!employee[EMAIL_KEY];
      const hasRole = !!employee[ROLE];
      if (!hasEmail && hasRole) {
        errors.userMissingEmailRows.push(rowIndex);
      }
    });

    // 9. Check if employees should be deactivated
    if (importTypes.override === importType) {
      existingEmployees.forEach((emp) => {
        const employeeIsOnList = !!employeesDict[emp.employeeId];
        if (!employeeIsOnList) {
          if (emp.enabled) {
            employeesToDeactivate.set(emp.employeeId, emp);
          }
          if (
            !isUserDisabled(emp?.user) &&
            !emp.user?.worktangoAdmin &&
            emp.user?.status.toLowerCase() === statuses.active
          ) {
            usersToDeactivate.set(emp.user.email, emp.user);
          }
        }
      });
    }

    // 10. Check if employees will get access
    validEmployees.forEach((importedEmployee) => {
      const importedEmployeeId = importedEmployee[COMPANY_EMPLOYEE_ID_KEY];
      const importedEmployeeRole = importedEmployee[ROLE];
      const firstName = importedEmployee[FIRST_NAME_KEY];
      const lastName = importedEmployee[LAST_NAME_KEY];
      const existingEmployee = existingEmployees.find((existing) => existing.employeeId === importedEmployeeId);

      // Should display a warning if the imported employee is new and it has a role
      if (!existingEmployee && !!importedEmployeeRole) {
        employeesToGrantAccess.set(importedEmployeeId, {
          role: importedEmployeeRole,
          fullName: `${firstName} ${lastName}`,
          employeeId: importedEmployeeId,
        });
      }

      // Should display a warning if:
      // - The employee already exists and does not have a role
      // - And, the imported employee has role
      if (existingEmployee && !existingEmployee.isUser && !existingEmployee.hasRole && !!importedEmployeeRole) {
        employeesToGrantAccess.set(importedEmployeeId, {
          role: importedEmployeeRole,
          fullName: `${firstName} ${lastName}`,
          employeeId: importedEmployeeId,
        });
      }
    });

    warnings.deactivateEmployees = Array.from(employeesToDeactivate.values());
    warnings.deactivateUsers = Array.from(usersToDeactivate.values());
    warnings.grantAccess = Array.from(employeesToGrantAccess.values());

    // Import is valid if it contains no validation errors.
    // This check expects each attribute of `errors` to be an array of errors
    // (or an empty array if no errors).
    const isValid = Object.keys(errors).reduce((validity, key) => {
      if (!validity) return validity;

      const hasErrors = errors[key].length > 0;

      return !hasErrors;
    }, true);

    // Format error messages into an array with mainError and subErrors,
    // The intention is to render a list of errors with sub-items if necessary.
    const errorsArray: { mainError: string; subErrors?: string[] }[] = [];
    Object.keys(errors).forEach((errorKey) => {
      if (!errors[errorKey]?.length) {
        return;
      }

      if (['unknownRoleNamesRows', 'customPermissionsRows', 'userMissingEmailRows'].includes(errorKey)) {
        // Displays the error in one line
        const msg = l10n.t(`employee.import.error.${errorKey}`, { rows: errors[errorKey].join(', ') });
        errorsArray.push({ mainError: msg, subErrors: [] });
      } else {
        // Displays the error in multiple lines
        const msgGroupHeader = l10n.t(`employee.import.error.${errorKey}`);
        errorsArray.push({ mainError: `${msgGroupHeader}:`, subErrors: errors[errorKey] });
      }
    });

    return { isValid, errorsArray, warnings };
  },
);

/**
 * Take an Excel file (specifically a `.xlsx` file format) containing a set of
 * employee-attributes referenced by exact name in the first row of columns
 * followed by one or more rows of employee data values to be imported to the
 * app database.
 *
 * If an employee already exists for a given ID, it will be overwritten.
 *
 * If a parsing error is encountered, the entire process will be stopped before
 * making any changes on the server.
 */
const importEmployees = createAsyncThunk<
  Notification | unknown,
  { importType: string; buffer: Buffer },
  { state: RootState; dispatch: AppDispatch }
>('employee/importEmployees', async ({ importType, buffer }, { getState, dispatch, rejectWithValue }) => {
  // TODO: Is there a better way of maintaining these key values?
  // WARNING: The expected key strings are explicitly defined here, but then
  // converted to lower case (along with all employee attribute keys) in order
  // to match without case sensitivity. This is not ideal and should be revised.
  const COMPANY_EMPLOYEE_ID_KEY = 'Employee Id'.toLowerCase();
  const LANGUAGE_KEY = 'Language'.toLowerCase();
  const ENABLED_KEY = 'Enabled'.toLowerCase();
  const ROLE_KEY = '$Survey_Role'.toLowerCase();

  const account = getState().account;
  const languages = getState().entities.languages;
  const attributes: EmployeeAttributeObject[] = Object.values(getState().entities.employeeAttributes);

  const employees: { [key: string]: string }[] = excel
    .employeesToJson(buffer, { dateFormat: 'YYYY-MM-DD' })
    .map((employee) => convert.toLowerKeys(employee))
    .filter((row) => !excel.isRowEmpty(row)); // This is done in order to match keys without case sensitivity.

  //Does a role column exist in the file?
  const hasRoleUpdate = employees.length > 0 && ROLE_KEY in employees[0];

  // Transform import data to format expected by backend (i.e., `EmployeeImportInput` GraphQL Type).
  const importableEmployees = employees.map((employee) => {
    const { [ENABLED_KEY]: isActive, ...employeeAttributeValues } = employee;

    // We only use lower case for all company employee IDs and email
    const companyEmployeeId = employee[COMPANY_EMPLOYEE_ID_KEY]?.toLowerCase();
    const enabled = validate.toBoolean(isActive);
    const languageId = coalesce(languages.codeIds[employeeAttributeValues[LANGUAGE_KEY]], null);

    const importableAttributeValues = attributes.reduce(
      (acc, attr) => {
        // Find first case-insensitive attribute key match.
        // TODO: This is kind of brittle; is there a more precise way of matching attributes?
        const matchingKey = Object.keys(employeeAttributeValues).find((key) => {
          return key === attr.name.toLowerCase();
        });

        if (!matchingKey) return acc;

        // Transform all email and companyEmployeeId values to lowercase to normalize
        // them and prevent name collisions with case sensitive values.
        // WARNING: should ID values be transformed? what if a customer wants to
        // explicitly use upper and lower case IDs like `83dEA-ePp24`?
        const value =
          (attr.type === attributeTypeKeys.EMAIL || attr.name.toLowerCase() === COMPANY_EMPLOYEE_ID_KEY) &&
          employeeAttributeValues?.[matchingKey]
            ? employeeAttributeValues?.[matchingKey].toLowerCase()
            : employeeAttributeValues?.[matchingKey];

        if (value === undefined) return acc;

        const updatedAttributeValue = {
          id: attr.id,
          value,
        };

        return [...acc, updatedAttributeValue];
      },
      [] as { id: number; value: string }[],
    );

    return {
      employeeId: companyEmployeeId,
      enabled,
      languageId,
      attributes: importableAttributeValues,
      ...(employee[ROLE_KEY] ? { role: employee[ROLE_KEY] } : {}),
    };
  });

  const { data: res } = await graphqlRequest<any>(ImportEmployeesDocument, {
    isUpdate: importType === importTypes.update,
    hasRoleUpdate,
    companyId: account.companyId,
    employees: importableEmployees,
  });
  const hasError = !res || !res.importEmployees?.isSuccess || res.error;

  if (hasError) {
    /* istanbul ignore start -- defensive: Apollo throws on null data, so this branch is unreachable */
    if (!res) {
      return rejectWithValue({
        title: l10n.t('employee.import.error.unableToImport'),
        message: l10n.t('error.somethingWentWrong'),
      });
    }
    /* istanbul ignore stop */

    if (!res?.importEmployees?.isSuccess && Array.isArray(res?.importEmployees?.conflicts)) {
      const errorMsg = res.importEmployees.conflicts.reduce((acc, conflictError) => {
        const { attributeId, attributeValue } = conflictError;
        const attribute = attributes.find((attr) => attr.id === Number(attributeId));
        const msg = attribute
          ? `${l10n.t('survey.duplicate')} ${attribute.name}: '${attributeValue}'`
          : `${l10n.t('survey.duplicate')} ${attributeId}: '${attributeValue}'`;

        // Separate each error message with a line break and a bullet point.
        return acc + `- ${msg}\n`;
      }, '');

      return rejectWithValue({
        title: l10n.t('employee.import.error.unableToImport'),
        message: errorMsg,
      });
    }

    return rejectWithValue(res?.error);
  }

  // Refresh the current employees page data if there were no errors.
  const {
    search,
    attributeValues: selectedAttributeValues,
    filterBy,
    orderBy,
    order,
    page,
    per,
  } = getState().ui.employees;

  dispatch(
    // @ts-ignore @TODO Epic WT-21600
    pageEmployees({
      attributeValues: selectedAttributeValues,
      filterBy,
      orderBy,
      // @ts-ignore @TODO Epic WT-21600
      order,
      page,
      per,
      search,
    }),
  );

  return {
    notification: {
      title: l10n.t('notifications.title.done'),
      message: l10n.t('employee.import.success'),
      status: notificationStatuses.success,
    },
  };
});

const genSampleUpdateEmployeeInput = createAction('employee/genSampleUpdateEmployeeInput', () => {
  const sampleInput = [
    {
      currentEmployeeId: 'tester@worktango.com',
      newEmployeeId: '123001',
    },
  ];

  const filename = 'Update-Employee-Ids-Sample';

  const employeesTab = {
    name: l10n.t('employee.upload.updateEmployeeIds'),
    titles: ['CurrentEmployeeId', 'NewEmployeeId'],
    rows: sampleInput.map((employeeIds) => [employeeIds.currentEmployeeId, employeeIds.newEmployeeId]),
  };

  // Will trigger browser download.
  excel.toFile(filename, [employeesTab]);

  return {
    payload: {
      filename,
    },
  };
});

const checkDuplication = (idPair, compareList) => {
  const { currentEmployeeId, newEmployeeId } = idPair;
  let duplicatedCurrentId;
  let duplicatedNewId;
  compareList.forEach((compareIdPair) => {
    if (currentEmployeeId === compareIdPair.currentEmployeeId) {
      duplicatedCurrentId = currentEmployeeId;
    }
    if (newEmployeeId === compareIdPair.newEmployeeId) {
      duplicatedNewId = newEmployeeId;
    }
  });
  return { duplicatedCurrentId, duplicatedNewId };
};

const validateUpdateEmployeeData = createAsyncThunk<
  { payload: { isValid: boolean; errorMsg?: string } } | unknown,
  { buffer: ArrayBuffer; companyId: number }
>('employee/validateUpdateEmployeeData', async ({ buffer, companyId }, { rejectWithValue }) => {
  const rawInputs = excel.employeeIdsToJson(buffer);
  const isValidDataFormat =
    Object.keys(rawInputs[0]).length === 2 && rawInputs[0].CurrentEmployeeId && rawInputs[0].NewEmployeeId;
  if (!isValidDataFormat) {
    return {
      isValid: false,
      errorMsg: '- Invalid input table format',
    };
  }
  const updateEmployeeIds = rawInputs.map((updateEmployeeIds) => ({
    currentEmployeeId: String(updateEmployeeIds.CurrentEmployeeId).toLowerCase(),
    newEmployeeId: String(updateEmployeeIds.NewEmployeeId).toLowerCase(),
  }));
  const { data: companyEmployeeIdsRes } = await graphqlRequest<any>(FetchCompanyEmployeeIdsDocument, { companyId });
  if (!companyEmployeeIdsRes?.employeeDetails?.results) {
    return rejectWithValue(`Unable to fetch company employee IDs for company ${companyId}`);
  }
  const companyEmployeeIdsResults = coalesce(companyEmployeeIdsRes?.employeeDetails?.results, []);
  const companyEmployeeIds = companyEmployeeIdsResults.map((employee) => employee.employeeId.toLowerCase());

  const dataErrors = {
    duplicatedCurrentIds: [] as string[],
    duplicatedNewIds: [] as string[],
    blankCurrentIds: [] as number[],
    blankNewIds: [] as number[],
    invalidCurrentIds: [] as string[],
    occupiedNewIds: [] as string[],
  };

  let existingIdFullSet = new Set(companyEmployeeIds);
  let existingIdSet = new Set(companyEmployeeIds);
  let rowNo = 2;
  updateEmployeeIds.forEach((updateEmployeeId) => {
    const remainingIds = updateEmployeeIds.slice(rowNo - 1);
    const currentId = updateEmployeeId.currentEmployeeId;
    const newId = updateEmployeeId.newEmployeeId;

    if (!currentId && !newId) {
      dataErrors.blankCurrentIds.push(rowNo);
      dataErrors.blankNewIds.push(rowNo);
      rowNo++;
      return;
    }

    if (!currentId) {
      dataErrors.blankCurrentIds.push(rowNo);
    } else if (!existingIdSet.has(currentId) && !dataErrors.invalidCurrentIds.includes(currentId)) {
      dataErrors.invalidCurrentIds.push(currentId);
    }
    if (!newId) {
      dataErrors.blankNewIds.push(rowNo);
    } else if (existingIdFullSet.has(newId) && !dataErrors.occupiedNewIds.includes(newId)) {
      dataErrors.occupiedNewIds.push(newId);
    }

    const { duplicatedCurrentId, duplicatedNewId } = checkDuplication(updateEmployeeId, remainingIds);
    if (duplicatedCurrentId && dataErrors.duplicatedCurrentIds.indexOf(duplicatedCurrentId) === -1) {
      dataErrors.duplicatedCurrentIds.push(duplicatedCurrentId);
    }
    if (duplicatedNewId && dataErrors.duplicatedNewIds.indexOf(duplicatedNewId) === -1) {
      dataErrors.duplicatedNewIds.push(duplicatedNewId);
    }

    rowNo++;
    if (currentId && !duplicatedCurrentId) existingIdSet.delete(currentId);
  });

  const isValid = Object.keys(dataErrors).reduce((validity, type) => {
    if (!validity) return validity;
    const hasErrors = dataErrors[type].length > 0;

    return !hasErrors;
  }, true);

  const errorMsg = isValid
    ? ''
    : Object.keys(dataErrors).reduce((accMsg, type) => {
        if (dataErrors[type].length === 0) return accMsg;

        const isBlankIdError = type === 'blankCurrentIds' || type === 'blankNewIds';
        const msgGroupHeader = isBlankIdError
          ? l10n.t(`employee.update-ids.error.${type}`) + ` (${l10n.t('employee.update-ids.error.row-no')})`
          : l10n.t(`employee.update-ids.error.${type}`);
        const msgGroup = dataErrors[type].reduce((acc, msg) => {
          return acc + `- ${msg}\n`;
        }, `${msgGroupHeader}:\n`);

        return accMsg + `${msgGroup}\n`;
      }, '');

  return { isValid, errorMsg };
});

const updateEmployeeIds = createAsyncThunk<
  Notification | unknown,
  { companyId: number; updatedEmployeeIds: { [key: string]: string }[] },
  { state: RootState; dispatch: AppDispatch }
>('employee/updateEmployeeIds', async ({ companyId, updatedEmployeeIds }, { getState, dispatch, rejectWithValue }) => {
  const validUpdatedIds = updatedEmployeeIds.map((updateEmployeeIds) => ({
    currentEmployeeId: String(updateEmployeeIds.CurrentEmployeeId).toLowerCase(),
    newEmployeeId: String(updateEmployeeIds.NewEmployeeId).toLowerCase(),
  }));
  try {
    const { data: res } = await graphqlRequest<any>(UpdateCompanyEmployeeIdsDocument, {
      companyId,
      updateEmployeeIdInput: validUpdatedIds,
    });

    if (res.updateCompanyEmployeeIds?.status === 'Failed') {
      const errorResult = res.updateCompanyEmployeeIds?.error;
      const errorMsg = `${l10n.t(errorResult.translationKey)}: ${errorResult.failedId}`;

      return rejectWithValue({
        title: l10n.t('employee.update-ids.error.unable-update'),
        message: errorMsg,
      });
    }
  } catch (error) {
    return rejectWithValue({
      title: l10n.t('employee.update-ids.error.unable-update'),
      message: l10n.t('error.somethingWentWrong'),
    });
  }

  // Refresh the current employees page data if there were no errors.
  const {
    search,
    attributeValues: selectedAttributeValues,
    filterBy,
    orderBy,
    order,
    page,
    per,
  } = getState().ui.employees;

  dispatch(
    // @ts-ignore @TODO Epic WT-21600
    pageEmployees({
      attributeValues: selectedAttributeValues,
      filterBy,
      orderBy,
      // @ts-ignore @TODO Epic WT-21600
      order,
      page,
      per,
      search,
    }),
  );

  return {
    notification: {
      title: l10n.t('notifications.title.done'),
      message: l10n.t('employee.update-ids.success'),
      status: notificationStatuses.success,
    },
  };
});

const validateUpdateIndividualEmployeeId = createAction(
  'employee/validateUpdateIndividualEmployeeId',
  ({ newEmployeeId, companyEmployeeIds }) => {
    const newId = String(newEmployeeId).toLowerCase();
    let isNewIdValid = false;

    const existingIdSet = new Set(companyEmployeeIds);
    if (!existingIdSet.has(newId)) {
      isNewIdValid = true;
    }

    const errorMsg = isNewIdValid ? '' : l10n.t('employee.update-ids.error.occupied-new-id');

    return {
      payload: {
        isValid: isNewIdValid,
        errorMsg,
      },
    };
  },
);

export const employeeActions = {
  fetchEmployee,
  fetchEmployees,
  generateSampleEmployeeImport,
  pageEmployees,
  modifyEmployee,
  createEmployee,
  removeEmployee,
  exportEmployees,
  importEmployees,
  validateEmployeesImportData,
  genSampleUpdateEmployeeInput,
  validateUpdateEmployeeData,
  updateEmployeeIds,
  validateUpdateIndividualEmployeeId,
};
